import { Inject } from 'vue-di-container';
import { Component, Prop, Vue } from 'vue-property-decorator';
import { Toaster } from '../../../Services/toast';
import WithRender from './TemplateRenewalRequests.html';
import MessageTemplateRequestDashboardService from '../MessageTemplateRequestDashboardService';
import { MessageTemplateRequestModel } from '../MessageTemplateRequestModel';
import { setPaymentReturnTarget } from '../../../constants/renewalPaymentRedirect';

@WithRender
@Component({
    diProvide: [
        MessageTemplateRequestDashboardService,
    ],
})
export default class TemplateRenewalRequests extends Vue {
    @Inject(MessageTemplateRequestDashboardService) public dashboardService!: MessageTemplateRequestDashboardService;
    @Prop({ type: Boolean, default: false })
    public layoutSup!: boolean;

    public requests: MessageTemplateRequestModel[] = [];
    public requestsCount: number = 0;
    public isLoading: boolean = false;
    public loadFailed: boolean = false;
    public filterModel: any = {
        displayId: '',
        templateName: '',
        senderName: '',
        clientName: '',
    };
    public pagination: any = { sortBy: 'createdDate', descending: true, page: 1, rowsPerPage: 10 };
    public headers: any = [
        { text: this.$t('dashboardPage.displayId'), sortable: true, value: 'displayId', align: 'center' },
        { text: this.$t('dashboardPage.templateName'), sortable: true, value: 'templateName', align: 'center' },
        { text: this.$t('dashboardPage.senderName'), sortable: true, value: 'senderName', align: 'center' },
        { text: this.$t('dashboardPage.clientName'), sortable: true, value: 'clientName', align: 'center' },
        { text: this.$t('shared.classification'), sortable: false, value: 'classification', align: 'center' },
        { text: this.$t('dashboardPage.requestDate'), sortable: true, value: 'createdDate', align: 'center' },
        { text: this.$t('shared.requestStatus'), sortable: false, value: 'requestStatus', align: 'center' },
        { text: this.$t('shared.actions'), sortable: false, align: 'center', width: '145' },
    ];
    private readonly renewMessageTemplateRequestType: number = 10;
    private readonly pendingPaymentStatus: string = '7';
    private readonly paidStatus: string = '4';
    private readonly rejectedStatus: string = '3';
    private readonly expiredStatus: string = '5';
    private searchTimeout: any = null;

    public get messageTemplatePaymentPhaseEnabled(): boolean {
        return !!(window.$config && window.$config.messageTemplatePaymentPhaseEnabled === true);
    }

    public mounted() {
        this.$emit('childinit', this.layoutSup);
    }

    public isPendingPayment(item: MessageTemplateRequestModel): boolean {
        return item.requestStatus.toString() === this.pendingPaymentStatus;
    }

    public isExpired(item: MessageTemplateRequestModel): boolean {
        const status = item.requestStatus.toString();
        return status === this.expiredStatus || status === this.rejectedStatus;
    }

    public getStatusText(item: MessageTemplateRequestModel): string {
        if (this.isPendingPayment(item)) {
            return this.$t('dashboardPage.renewalStatusPendingPayment').toString();
        }
        if (this.isExpired(item)) {
            return this.$t('dashboardPage.renewalStatusExpired').toString();
        }
        return this.$t('dashboardPage.renewalStatusPaid').toString();
    }

    public getStatusColor(item: MessageTemplateRequestModel): string {
        if (this.isPendingPayment(item)) {
            return '#aaa7a7';
        }
        if (this.isExpired(item)) {
            return '#014bb1';
        }
        return '#5aaa0b';
    }

    public changeSort(column: any) {
        const header = this.headers.find((h: any) => h.value === column);
        if (!header || !header.sortable) {
            return;
        }
        if (this.pagination.sortBy === column) {
            this.pagination.descending = !this.pagination.descending;
        } else {
            this.pagination.sortBy = column;
            this.pagination.descending = false;
        }
        this.pagination.page = 1;
        this.search();
    }

    public onInputSearch() {
        if (this.searchTimeout) {
            clearTimeout(this.searchTimeout);
        }
        this.searchTimeout = setTimeout(() => {
            this.pagination.page = 1;
            this.search();
        }, 1000);
    }

    public async reset() {
        this.filterModel.displayId = '';
        this.filterModel.templateName = '';
        this.filterModel.senderName = '';
        this.filterModel.clientName = '';
        this.pagination.page = 1;
        await this.search();
    }

    public async updatePaginate(val: any) {
        this.pagination.page = val.page;
        this.pagination.rowsPerPage = val.rowsPerPage;
        await this.search();
    }

    public async search() {
        this.isLoading = true;
        const response = await this.dashboardService.Get(this.buildSearchModel());
        this.isLoading = false;
        if (!response || !response.isSuccess) {
            this.loadFailed = true;
            this.requests = [];
            this.requestsCount = 0;
            Toaster.error(response && response.errorMessage
                ? response.errorMessage
                : this.$t('shared.errorOccurred').toString());
            return;
        }
        this.loadFailed = false;
        this.requests = response.result || [];
        this.requestsCount = response.totalCount || 0;
    }

    public pay(id: string) {
        setPaymentReturnTarget('templateRenewal');
        this.$router.push({ name: 'Payment', params: { ids: id }, query: { fromMessageTemplate: '1' } });
    }

    public canPay(item: MessageTemplateRequestModel): boolean {
        return this.isPendingPayment(item) && this.messageTemplatePaymentPhaseEnabled;
    }

    public details(id: string) {
        this.$router.push({
            name: 'MessageTemplateRequestInfo',
            params: { id },
            query: { from: 'TemplateRenewalRequests' },
        });
    }

    private buildSearchModel() {
        return {
            templateName: this.filterModel.templateName || '',
            senderName: this.filterModel.senderName || '',
            displayId: this.filterModel.displayId || '',
            clientName: this.filterModel.clientName || '',
            requestStatuses: [this.pendingPaymentStatus, this.paidStatus, this.rejectedStatus, this.expiredStatus].join(','),
            requestType: this.renewMessageTemplateRequestType,
            senderStatus: 0,
            sortDirection: this.pagination.descending,
            sortBy: this.pagination.sortBy || 'createdDate',
            pageIndex: (this.pagination.page || 1) - 1,
            pagesize: this.pagination.rowsPerPage || 10,
        };
    }
}
